/**
 * Awana 등록교회 찾기 지도의 순수 로직.
 *
 * 데이터는 Google Apps Script 가 공개하는 Google Sheet 를 그대로 받아오므로
 * 위/경도가 문자열이거나 비어 있을 수 있다. 여기서는 그 지저분함만 흡수하고
 * 지도(naver.maps) 에는 의존하지 않는다.
 */

export interface PublicChurch {
  registrationNumber?: string;
  name?: string;
  region?: string;
  address?: string;
  postalCode?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  clubs?: string;
}

export interface ChurchFilters {
  keyword: string;
  region: string;
  club: string;
}

export interface NearbyChurch extends PublicChurch {
  /** 기준 위치로부터의 거리(km) */
  distance: number;
}

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return NaN;
  return Number(value);
};

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** 시트에서 온 위/경도 한 칸을 숫자로. 비었거나 숫자가 아니면 null. */
export function parseCoordinate(value: unknown): number | null {
  const parsed = toNumber(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** 인포윈도우의 "네이버 지도에서 보기" 링크. */
export function naverMapSearchUrl(church: PublicChurch): string {
  const query = [church.name, church.address].filter(Boolean).join(' ');
  return `https://map.naver.com/p/search/${encodeURIComponent(query)}`;
}

/** 지역 드롭다운에서 앞자리를 차지하는 익숙한 순서. 여기 없는 지역도 버리지 않는다. */
const FAMILIAR_REGIONS = ['서울', '경기', '인천', '강원', '충청', '전라', '경상', '제주'];

/**
 * 지역 선택지를 실제 데이터에서 만든다.
 * 하드코딩하면 시트에 새 지역이 생겼을 때(예: '해외') 그 교회들이
 * 어떤 필터로도 도달할 수 없게 된다.
 */
export function regionOptions(churches: PublicChurch[]): string[] {
  const found = Array.from(
    new Set(churches.map((church) => church.region).filter((region): region is string => !!region))
  );

  return found.sort((a, b) => {
    const indexA = FAMILIAR_REGIONS.indexOf(a);
    const indexB = FAMILIAR_REGIONS.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b, 'ko');
  });
}

/** 하버사인 거리(km). 좌표 중 하나라도 유한한 수가 아니면 NaN. */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  if (![lat1, lon1, lat2, lon2].every((value) => Number.isFinite(value))) return NaN;

  const earthRadiusKm = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** 교회명·주소 키워드, 지역, 운영클럽으로 거른다. 빈 조건은 통과시킨다. */
export function filterChurches(churches: PublicChurch[], filters: ChurchFilters): PublicChurch[] {
  const keyword = filters.keyword.trim().toLowerCase();
  const { region } = filters;
  const club = filters.club.trim().toUpperCase();

  return churches.filter((church) => {
    const searchText = `${church.name || ''} ${church.address || ''}`.toLowerCase();
    const keywordOK = !keyword || searchText.includes(keyword);
    const regionOK = !region || church.region === region;

    // 시트의 clubs 는 "PUGGLES, SPARKS, T&T" 같은 쉼표 목록이다.
    const clubs = String(church.clubs || '')
      .split(',')
      .map((item) => item.trim().toUpperCase());
    const clubOK = !club || clubs.includes(club);

    return keywordOK && regionOK && clubOK;
  });
}

/** 기준 좌표에서 가까운 순으로 limit 개. 좌표가 없는 교회는 빠진다. */
export function nearestChurches(
  churches: PublicChurch[],
  myLat: number,
  myLng: number,
  limit: number
): NearbyChurch[] {
  return churches
    .map((church) => ({
      ...church,
      distance: distanceKm(myLat, myLng, toNumber(church.latitude), toNumber(church.longitude)),
    }))
    .filter((church) => Number.isFinite(church.distance))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
}
